import React from 'react';
import CaseStudyTemplate from './CaseStudyTemplate';

export default function CaseStudyVShred() {
  const summary = (
    <div>
      <p>Led the strategic redesign of V Shred's flagship mobile fitness application to improve user engagement and workout completion rates. The project focused on creating a more intuitive user experience while maintaining the brand's high-energy, results-driven approach. Through comprehensive user research and iterative design processes, we delivered a solution that significantly increased user retention and satisfaction.</p>
      
      <div className="key-metrics">
        <div className="metric-item">
          <span className="metric-number">+40%</span>
          <span className="metric-label">User Engagement</span>
        </div>
        <div className="metric-item">
          <span className="metric-number">+25%</span>
          <span className="metric-label">Workout Completion</span>
        </div>
        <div className="metric-item">
          <span className="metric-number">4.8/5</span>
          <span className="metric-label">App Store Rating</span>
        </div>
      </div>
    </div>
  );

  const challenge = (
    <div>
      <p>V Shred's existing mobile app suffered from low user retention and poor workout completion rates. Users were dropping off during their fitness journeys, citing confusion with navigation, overwhelming workout options, and lack of clear progress tracking. The business needed a solution that would increase user engagement while staying true to their bold, motivational brand identity.</p>
      
      <div className="challenge-points">
        <div className="challenge-point">
          <strong>Low Retention:</strong> 60% of users churned within the first month
        </div>
        <div className="challenge-point">
          <strong>Poor Navigation:</strong> Users couldn't easily find workouts suited to their level
        </div>
        <div className="challenge-point">
          <strong>Lack of Progress Tracking:</strong> No clear way to visualize fitness improvements
        </div>
      </div>
    </div>
  );

  const roleAndApproach = (
    <div>
      <p>As the Senior Digital Product Strategist, I led the end-to-end redesign process, from initial research through implementation. My approach combined quantitative data analysis with qualitative user insights to drive strategic decisions.</p>
      
      <div className="approach-methodology">
        <div className="methodology-step">
          <h4>Research & Analysis</h4>
          <p>Conducted user interviews, analyzed app analytics, and performed competitive analysis to understand pain points and opportunities.</p>
        </div>
        <div className="methodology-step">
          <h4>Strategy Development</h4>
          <p>Created user personas, mapped customer journeys, and defined key success metrics aligned with business goals.</p>
        </div>
        <div className="methodology-step">
          <h4>Design & Testing</h4>
          <p>Led iterative design sprints, created prototypes, and conducted usability testing to validate solutions.</p>
        </div>
      </div>
    </div>
  );

  const findings = (
    <div>
      <p>Through extensive user research and data analysis, we uncovered key insights that shaped our redesign strategy:</p>
      
      <div className="findings-list">
        <div className="finding-item">
          <h4>Motivation Over Complexity</h4>
          <p>Users preferred simple, motivational content over complex workout tracking features. They wanted to feel inspired and guided rather than overwhelmed by data.</p>
        </div>
        <div className="finding-item">
          <h4>Social Proof Drives Action</h4>
          <p>Success stories and community features significantly increased user engagement and workout completion rates.</p>
        </div>
        <div className="finding-item">
          <h4>Progressive Difficulty Essential</h4>
          <p>Users needed clear workout progression paths that adapted to their fitness level and goals, not one-size-fits-all solutions.</p>
        </div>
      </div>
    </div>
  );

  const outcomes = (
    <div>
      <p>Our strategic approach resulted in comprehensive deliverables that guided the entire redesign process:</p>
      
      <div className="deliverables-grid">
        <div className="deliverable-item">
          <h4>+40% User Engagement</h4>
          <p>Significant increase in daily active users and session duration through improved UX design.</p>
        </div>
        <div className="deliverable-item">
          <h4>+25% Workout Completion</h4>
          <p>Higher completion rates achieved through better motivation and progress tracking features.</p>
        </div>
        <div className="deliverable-item">
          <h4>4.8/5 App Store Rating</h4>
          <p>Dramatic improvement in user satisfaction reflected in app store reviews and ratings.</p>
        </div>
        <div className="deliverable-item">
          <h4>Reduced Churn Rate</h4>
          <p>60% reduction in first-month churn through improved onboarding and user guidance.</p>
        </div>
      </div>
    </div>
  );

  const keyDeliverables = (
    <div>
      <p>Our strategic approach resulted in comprehensive deliverables that guided the entire redesign process:</p>
      
      <div className="deliverables-grid">
        <div className="deliverable-item">
          <h4>User Research Report</h4>
          <p>Comprehensive analysis of user behavior patterns, pain points, and opportunities based on 50+ user interviews and app analytics.</p>
        </div>
        <div className="deliverable-item">
          <h4>Product Strategy Framework</h4>
          <p>Strategic roadmap aligning user needs with business objectives, including success metrics and implementation timeline.</p>
        </div>
        <div className="deliverable-item">
          <h4>UX/UI Design System</h4>
          <p>Complete design system with wireframes, prototypes, and style guide ensuring consistent user experience across all touchpoints.</p>
        </div>
        <div className="deliverable-item">
          <h4>Implementation Guide</h4>
          <p>Detailed documentation and specifications for development team, including user stories and acceptance criteria.</p>
        </div>
      </div>
    </div>
  );

  const lessonsLearned = (
    <div>
      <p>This project provided valuable insights that continue to inform my approach to digital product strategy:</p>
      
      <div className="lessons-list">
        <div className="lesson-item">
          <h4>User Empathy Over Assumptions</h4>
          <p>Early assumptions about user needs were often incorrect. Direct user research and continuous feedback loops were essential for creating solutions that truly resonated with the target audience.</p>
        </div>
        <div className="lesson-item">
          <h4>Incremental Progress Beats Perfection</h4>
          <p>Releasing iterative improvements and measuring results proved more effective than trying to perfect everything before launch. User feedback guided better solutions than extended development cycles.</p>
        </div>
        <div className="lesson-item">
          <h4>Cross-functional Collaboration is Key</h4>
          <p>The most successful features emerged from close collaboration between strategy, design, development, and marketing teams. Regular alignment sessions prevented costly late-stage changes.</p>
        </div>
      </div>
    </div>
  );

  return (
    <CaseStudyTemplate
      clientName="V Shred"
      title="Flagship Mobile App"
      subtitle="Designing a flagship training app with a multi-tier membership strategy"
      heroImage={require('../assets/vshred.png')}
      heroImageAlt="VShred App"
      summary={summary}
      challenge={challenge}
      roleAndApproach={roleAndApproach}
      findings={findings}
      outcomes={outcomes}
      keyDeliverables={keyDeliverables}
      lessonsLearned={lessonsLearned}
    />
  );
} 